import type { ReactNode } from "react";
import { Link } from "react-router-dom";

interface StatCardProps {
  label: string;
  value: number | string;
  icon: string;
  tone?: "primary" | "accent" | "secondary" | "neutral";
  hint?: string;
  to?: string;
}

const tones: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-900",
  neutral: "bg-background-200 text-foreground-600",
};

export default function StatCard({
  label,
  value,
  icon,
  tone = "primary",
  hint,
  to,
}: StatCardProps) {
  const inner: ReactNode = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-label font-semibold uppercase tracking-[0.12em] text-foreground-500">
          {label}
        </span>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tones[tone]}`}>
          <i className={`${icon} text-lg leading-none`} aria-hidden="true"></i>
        </span>
      </div>
      <p className="mt-3 text-3xl font-heading font-bold text-foreground-950">{value}</p>
      {hint && <p className="mt-1 text-xs text-foreground-500">{hint}</p>}
    </>
  );

  const className =
    "block rounded-lg border border-background-200 bg-background-50 p-5 transition-colors duration-200";

  if (to) {
    return (
      <Link to={to} className={`${className} cursor-pointer hover:border-primary-300`}>
        {inner}
      </Link>
    );
  }

  return <div className={className}>{inner}</div>;
}